import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Plane, Video, Phone, MessageSquare, AlertCircle } from "lucide-react";
import { lawyerApi } from "@lawyer/services/lawyerApi";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { describeState, fmtCountdown, fmtIstDate, fmtSlotTime } from "@lawyer/components/mobile/statusUtils";
import { requestDashboardReload } from "@lawyer/constants/events";

const TYPE_ICON = { video: Video, audio: Phone, chat: MessageSquare };
const TONE = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  rose: "border-rose-200 bg-rose-50 text-rose-900",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  slate: "border-slate-200 bg-white text-slate-900",
};

const LIVE_STATUSES = ["live", "waiting_for_user", "waiting_for_lawyer"];
const isLive = (c) => LIVE_STATUSES.includes(c?.status);
// Paid instant booking the lawyer hasn't started yet — it keeps the lawyer "busy" and auto-closes 10 min after payment.
const isInstantWaiting = (c) => c?.booking_type === "instant" && ["pending", "confirmed", "accepted"].includes(c?.status);
const CLOSED = ["completed", "cancelled", "rejected", "no_show", "lawyer_no_show"];

// Countdown for the current consultation, read from the server (never a local elapsed clock):
// live → timer end; unstarted instant → the lawyer no-show deadline.
function useRemaining(cid) {
  const [endAt, setEndAt] = useState(null);
  const [phase, setPhase] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!cid) { setEndAt(null); setPhase(null); return undefined; }
    let stop = false;
    let closed = false;
    const load = () => lawyerApi.get(`/consultations/${cid}`).then((r) => {
      if (stop) return;
      // Session ended / auto-closed → re-fetch the dashboard so the card leaves the busy state (socket-less fallback).
      if (CLOSED.includes(r.data?.status) && !closed) { closed = true; requestDashboardReload(); return; }
      const t = r.data?.timer || {};
      const ns = r.data?.lawyer_no_show;
      setEndAt(t.consultation_end_at || t.final_end_at || (ns?.applies ? ns.deadline_utc : null) || null);
      setPhase(t.started_at ? "live" : isInstantWaiting(r.data) ? "waiting" : "not_started");
    }).catch(() => {});
    load();
    const poll = setInterval(load, 30000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { stop = true; clearInterval(poll); clearInterval(tick); };
  }, [cid]);
  // Countdown reached zero → ask the server once (the sweep settles within ~20 s) instead of waiting for the next 30 s poll.
  const remaining = endAt ? (new Date(endAt).getTime() - now) / 1000 : null;
  const hitZero = remaining != null && remaining <= 0;
  useEffect(() => {
    if (!cid || !hitZero) return undefined;
    const id = setTimeout(() => lawyerApi.get(`/consultations/${cid}`).then((r) => { if (CLOSED.includes(r.data?.status)) requestDashboardReload(); }).catch(() => {}), 25000);
    return () => clearTimeout(id);
  }, [cid, hitZero]);
  return { remaining, phase };
}

// Busy but the live booking isn't in today's agenda (e.g. booked across the IST/UTC date line): use the live bucket.
function useLiveFallback(enabled) {
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!enabled) { setLive(null); return undefined; }
    let stop = false;
    lawyerApi.get("/lawyer/consultations", { params: { bucket: "live", limit: 1 } })
      .then((r) => { if (!stop) setLive((r.data?.items || [])[0] || null); }).catch(() => {});
    return () => { stop = true; };
  }, [enabled]);
  return live;
}

// Next bookable slot — same public computation clients see for this lawyer.
// `initial` (from the dashboard payload) short-circuits the fetch; undefined = self-load.
export function useNextAvailable(lawyerId, enabled, initial) {
  const [next, setNext] = useState(undefined);
  const hasInitial = initial !== undefined;
  useEffect(() => {
    if (hasInitial || !enabled || !lawyerId) return undefined;
    let stop = false;
    lawyerApi.get(`/lawyers/${lawyerId}`).then((r) => { if (!stop) setNext(r.data?.next_available || null); }).catch(() => { if (!stop) setNext(null); });
    return () => { stop = true; };
  }, [lawyerId, enabled, hasInitial]);
  return hasInitial ? initial : next;
}

export function nextLabel(next) {
  if (next === undefined) return "Checking…";
  if (!next) return "No slots in your schedule yet";
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const day = next.date === today ? "Today" : fmtIstDate(`${next.date}T00:00:00+05:30`);
  return `${day}, ${fmtSlotTime(next.time)}`;
}

// `wide` = desktop variant: text on the left, actions on the right (same data/handlers).
export default function MobileStatusCard({ lawyer, availability, todayAgenda = [], onAvailabilityToggle, onVacationToggle, wide = false, nextAvailable }) {
  const state = availability?.state || (lawyer.vacation_mode ? "vacation" : lawyer.available ? "online_manual" : "offline");
  const agendaLive = todayAgenda.find(isLive) || todayAgenda.find(isInstantWaiting);
  const fallbackLive = useLiveFallback(state === "busy" && !agendaLive);
  const live = agendaLive || fallbackLive;
  const { remaining, phase } = useRemaining(state === "busy" ? live?.id : null);
  const ownNext = useNextAvailable(lawyer.id, nextAvailable === undefined && state === "offline" && lawyer.listed);
  const next = nextAvailable === undefined ? ownNext : nextAvailable;
  const d = describeState(state, availability);
  const Icon = TYPE_ICON[live?.consultation_type] || Video;

  if (lawyer.listed === false) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="m-status-card" data-state="unlisted">
        <p className="text-xs font-extrabold tracking-wide text-slate-500">NOT LISTED YET</p>
        <p className="mt-1 text-sm text-slate-600">Online and vacation controls unlock once your profile is verified and listed.</p>
      </div>
    );
  }

  if (wide) return <WideStatusCard state={state} d={d} live={live} remaining={remaining} phase={phase} next={next} lawyer={lawyer} Icon={Icon} onAvailabilityToggle={onAvailabilityToggle} onVacationToggle={onVacationToggle} />;

  return (
    <div className={`rounded-2xl border p-4 ${TONE[d.tone]}`} data-testid="m-status-card" data-state={state}>
      <div className="flex items-center gap-2">
        {state === "vacation" ? <Plane size={16} className="text-amber-600" /> : <span className={`h-3 w-3 rounded-full ${d.dot}`} />}
        <p className="text-sm font-extrabold tracking-wide flex-1" style={{ fontFamily: "Poppins" }} data-testid="m-status-label">{d.label}</p>
        <Link to={`${LAWYER_BASE}/availability`} aria-label="Availability" className="opacity-60"><ChevronRight size={18} /></Link>
      </div>

      {(state === "online_scheduled" || state === "online_manual") && (
        <>
          <p className="mt-2 text-sm">{d.detail}</p>
          <p className="text-sm">Instant consultations: <span className="font-bold">ON</span></p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button type="button" onClick={onAvailabilityToggle} disabled={state === "online_scheduled"}
              className="h-11 rounded-xl border border-slate-300 bg-white text-sm font-bold text-[#0A2540] disabled:opacity-50" data-testid="m-go-offline"
              title={state === "online_scheduled" ? "You're inside scheduled hours — edit your schedule to go offline" : undefined}>
              Go Offline
            </button>
            <Link to={`${LAWYER_BASE}/availability`} className="h-11 rounded-xl bg-[#0A2540] text-white text-sm font-bold grid place-items-center" data-testid="m-manage-availability">
              Manage Availability
            </Link>
          </div>
          {state === "online_scheduled" && <p className="mt-2 text-[11px] opacity-70">Scheduled hours keep you online — change them under Manage Availability.</p>}
        </>
      )}

      {state === "busy" && (
        <>
          <BusyDetail live={live} remaining={remaining} phase={phase} testId="m-remaining" />
          <div className="mt-3 rounded-xl bg-rose-100/70 border border-rose-200 px-3 py-2 flex items-start gap-2 text-xs text-rose-900" data-testid="m-busy-note">
            <AlertCircle size={14} className="shrink-0 mt-0.5 text-rose-600" /> No new instant consultations until this one ends.
          </div>
          {live && (
            <Link to={`${LAWYER_BASE}/consultations/${live.id}/room`} className="mt-3 h-12 rounded-xl bg-rose-600 text-white text-sm font-extrabold flex items-center justify-center gap-2" data-testid="m-open-room">
              <Icon size={16} /> {roomLabel(live)} <ArrowRight size={16} />
            </Link>
          )}
        </>
      )}

      {state === "offline" && (
        <>
          <p className="mt-2 text-sm text-slate-600">Next availability: <span className="font-bold text-slate-900" data-testid="m-next-available">{nextLabel(next)}</span></p>
          <button type="button" onClick={onAvailabilityToggle} className="mt-3 w-full h-12 rounded-xl bg-[#0A2540] text-white text-sm font-extrabold" data-testid="m-go-online">
            Go Online
          </button>
          <Link to={`${LAWYER_BASE}/availability`} className="mt-2 w-full h-10 inline-flex items-center justify-center gap-1 text-sm font-bold text-[#0A2540]" data-testid="m-manage-availability">
            Manage Availability <ArrowRight size={14} />
          </Link>
        </>
      )}

      {state === "vacation" && (
        <>
          <p className="mt-2 text-sm">{lawyer.vacation_until ? `Back on ${fmtIstDate(lawyer.vacation_until)}` : "Clients can't book you while you're away"}</p>
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={onVacationToggle} className="h-11 px-5 rounded-xl border border-amber-300 bg-white text-sm font-bold text-[#0A2540]" data-testid="m-end-vacation">
              End Vacation
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const LABEL_TONE = { emerald: "text-emerald-800", rose: "text-rose-700", amber: "text-amber-800", slate: "text-slate-700" };

const roomLabel = (live) => (isInstantWaiting(live) ? "Start Consultation" : "Open Consultation Room");

// Busy copy: who it's with + a server-driven countdown (time left in a live session, or time left to start an instant one).
function BusyDetail({ live, remaining, phase, testId }) {
  const waiting = isInstantWaiting(live);
  return (
    <>
      <p className="mt-2 text-sm" data-testid="busy-detail">
        {live ? <>{waiting && "Instant "}<span className={waiting ? "" : "capitalize"}>{live.consultation_type}</span> consultation with {live.user_name || "client"}{waiting && " · client is waiting"}</> : "A consultation is in progress"}
      </p>
      {phase === "not_started" && <p className="text-sm font-bold text-rose-700" data-testid={testId} data-phase="not_started">Timer starts when you join the {live?.consultation_type === "chat" ? "chat" : "call"}</p>}
      {phase !== "not_started" && remaining != null && (
        <p className="text-sm font-bold text-rose-700 tabular-nums" data-testid={testId} data-phase={waiting ? "waiting" : "live"}>
          {fmtCountdown(remaining)} {waiting ? "to start · auto-closes with a refund after that" : "remaining"}
        </p>
      )}
    </>
  );
}
const btnOutline = "h-11 px-5 rounded-xl border border-slate-300 bg-white text-sm font-bold text-[#0A2540] hover:border-[#0A2540] disabled:opacity-50 inline-flex items-center justify-center gap-2";
const btnSolid = "h-11 px-5 rounded-xl bg-[#0A2540] text-white text-sm font-bold hover:bg-[#0c2f52] inline-flex items-center justify-center gap-2";

function WideStatusCard({ state, d, live, remaining, phase, next, lawyer, Icon, onAvailabilityToggle, onVacationToggle }) {
  const online = state === "online_scheduled" || state === "online_manual";
  return (
    <div className={`rounded-2xl border px-6 py-5 flex flex-col ${TONE[d.tone]}`} data-testid="d-status-card" data-state={state}>
      <div className="flex items-center gap-3">
        {state === "vacation" ? <Plane size={22} className="text-amber-600" /> : <span className={`block h-4 w-4 rounded-full ${d.dot}`} />}
        <p className={`flex-1 text-lg font-extrabold tracking-wide ${LABEL_TONE[d.tone]}`} style={{ fontFamily: "Poppins" }} data-testid="d-status-label">{d.label}</p>
        <Link to={`${LAWYER_BASE}/availability`} aria-label="Availability" className="opacity-60 hover:opacity-100"><ChevronRight size={20} /></Link>
      </div>
      <div className="mt-2 pl-7 flex-1">
        {online && (
          <>
            <p className="mt-1 text-sm">{d.detail}</p>
            <p className="text-sm">Instant consultations: <span className="font-bold text-emerald-700">ON</span></p>
          </>
        )}
        {state === "busy" && <BusyDetail live={live} remaining={remaining} phase={phase} testId="d-remaining" />}
        {state === "offline" && <p className="mt-1 text-sm text-slate-600">Next availability: <span className="font-bold text-slate-900" data-testid="d-next-available">{nextLabel(next)}</span></p>}
        {state === "vacation" && <p className="mt-1 text-sm">{lawyer.vacation_until ? `Back on: ${fmtIstDate(lawyer.vacation_until)}` : "Clients can't book you while you're away"}</p>}
      </div>
      <div className="mt-4 pl-7 flex items-center gap-3 flex-wrap">
        {online && (
          <>
            <button type="button" onClick={onAvailabilityToggle} disabled={state === "online_scheduled"} className={btnOutline} data-testid="d-go-offline"
              title={state === "online_scheduled" ? "You're inside scheduled hours — edit your schedule to go offline" : undefined}>Go Offline</button>
            <Link to={`${LAWYER_BASE}/availability`} className={btnSolid} data-testid="d-manage-availability">Manage Availability</Link>
          </>
        )}
        {state === "busy" && live && (
          <Link to={`${LAWYER_BASE}/consultations/${live.id}/room`} className="h-11 px-5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-extrabold inline-flex items-center gap-2" data-testid="d-open-room">
            <Icon size={16} /> {roomLabel(live)} <ArrowRight size={16} />
          </Link>
        )}
        {state === "offline" && (
          <>
            <button type="button" onClick={onAvailabilityToggle} className={btnSolid} data-testid="d-go-online">Go Online</button>
            <Link to={`${LAWYER_BASE}/availability`} className={btnOutline} data-testid="d-manage-availability">Manage Availability</Link>
          </>
        )}
        {state === "vacation" && (
          <button type="button" onClick={onVacationToggle} className="h-11 px-5 rounded-xl border border-amber-300 bg-white text-sm font-bold text-[#0A2540] hover:border-amber-500" data-testid="d-end-vacation">End Vacation</button>
        )}
      </div>
    </div>
  );
}
